import { motion, AnimatePresence, useMotionValue, animate as animateMv } from "framer-motion";
import { useState, useEffect, useRef, useCallback } from "react";
import AgentVisual from "./AgentVisuals";
import AgentLiveDemo from "./AgentLiveDemo";
import { useActiveOnScreen } from "@/lib/useActiveOnScreen";

const AGENT_DEMO_KEYS = {};

const agents = [
    {
        name: "Intelligent Reception",
        icon: "fa-phone-volume",
        tagline: "Answers every call. Routes every caller. Captures every detail.",
        bullets: [
            "Identifies new clients vs. known adjusters/providers",
            "Routes intelligently across teams and queues",
            "Logs structured intake data into your CMS in real time",
        ],
        tint: "blue",
    },
    {
        name: "Lead Qualification",
        icon: "fa-filter",
        tagline: "Screens every new lead — inbound and outbound — against your firm's criteria.",
        bullets: [
            "Multi-channel: voice, SMS, web form",
            "Applies your exact qualification logic",
            "Hands off cleanly with a complete summary",
        ],
        tint: "neutral",
    },
    {
        name: "Matter Completion",
        icon: "fa-folder-tree",
        tagline: "Monitors open matters for missing data and quietly fills the gaps.",
        bullets: [
            "Extracts facts from documents and calls",
            "Triggers retrieval workflows automatically",
            "Keeps your CRM complete with zero chase work",
        ],
        tint: "blue",
    },
    {
        name: "Chaser Agent",
        icon: "fa-rotate-right",
        tagline: "Follows up persistently on every pending action — by call, SMS, and email.",
        bullets: [
            "Multi-touch sequences with built-in escalation",
            "Knows when to chase and when to back off",
            "Closes the loop with structured logs in your CMS",
        ],
        tint: "neutral",
    },
    {
        name: "Document Follow-up",
        icon: "fa-file-signature",
        tagline: "Chases missing signatures, retainers, and required documents until they're returned.",
        bullets: [
            "E-signature retry workflows automated",
            "Tracks document state across CMS, Outlook, and DMS",
            "Auto-reminds clients across voice, SMS, and email",
        ],
        tint: "blue",
    },
    {
        name: "Medical Records Retrieval",
        icon: "fa-file-medical",
        tagline: "Requests, tracks, and processes medical records from providers — start to finish.",
        bullets: [
            "Auto-generates and routes HIPAA requests",
            "Tracks provider response, follows up on its own",
            "Extracts structured data ready for case prep",
        ],
        tint: "neutral",
    },
    {
        name: "Claims Opening",
        icon: "fa-clipboard-check",
        tagline: "Opens claims with carriers cleanly — without the back-and-forth.",
        bullets: [
            "Files first notice of loss across major carriers",
            "Tracks claim numbers and adjuster assignment",
            "Routes confirmations directly into the matter",
        ],
        tint: "blue",
    },
    {
        name: "Adjuster Agent",
        icon: "fa-handshake",
        tagline: "Communicates with insurance adjusters on your team's behalf — voice and email.",
        bullets: [
            "Logs every interaction back to the case file",
            "Negotiates demand follow-ups on a schedule you set",
            "Escalates to attorneys only when judgment is needed",
        ],
        tint: "neutral",
    },
    {
        name: "More shipping every month",
        icon: "fa-wand-magic-sparkles",
        tagline: "We're always cooking. New agents go into production every month — and if your bottleneck isn't on this list yet, we build the agent for it. Handcrafted to your firm's workflows.",
        bullets: [
            "Tell us the work that's burying your team",
            "We design, build, and ship the agent in weeks — not quarters",
            "Cooking next: Demand Letter Drafting, Settlement Calculator, Lien Resolution",
        ],
        tint: "soon",
    },
];

const ROTATE_INTERVAL_MS = 5000;

const AgentsSection = () => {
    const [active, setActive] = useState(0);
    const [direction, setDirection] = useState(1);
    const [paused, setPaused] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [demoOpen, setDemoOpen] = useState(false);
    const [demoMinimized, setDemoMinimized] = useState(false);
    const [demoAgent, setDemoAgent] = useState(null); // { key, name }
    const timerRef = useRef(null);
    const { ref: sectionRef, active: onScreen } = useActiveOnScreen();
    const swipeX = useMotionValue(0); // wrapper x — driven by drag + imperative animate

    const total = agents.length;

    // Detect mobile/tablet (no `lg:` cursor or hover). Updates on resize.
    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const mq = window.matchMedia("(max-width: 1023px)");
        const update = () => setIsMobile(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    const goTo = useCallback((next) => {
        setDirection(next > active ? 1 : -1);
        setActive(((next % total) + total) % total);
    }, [active, total]);

    const next = useCallback(() => {
        setDirection(1);
        setActive((a) => (a + 1) % total);
    }, [total]);

    const prev = useCallback(() => {
        setDirection(-1);
        setActive((a) => (a - 1 + total) % total);
    }, [total]);

    // Auto-rotate — paused while hovered/dragged AND while the section is off-screen
    useEffect(() => {
        if (paused || !onScreen || demoOpen || demoMinimized) return undefined;
        timerRef.current = setInterval(() => {
            setDirection(1);
            setActive((a) => (a + 1) % total);
        }, ROTATE_INTERVAL_MS);
        return () => clearInterval(timerRef.current);
    }, [paused, total, onScreen, demoOpen, demoMinimized]);

    // Keyboard arrows when section is in focus
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [next, prev]);

    const agent = agents[active];

    return (
        <section ref={sectionRef} id="agents" data-testid="agents-section" className="relative py-24 md:py-32">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mb-10 md:mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
                >
                    <div className="max-w-3xl">
                        <span className="eyebrow-pill">Agents in production</span>
                        <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5">
                            Meet the agents <span className="accent-blue">running in firms today.</span>
                        </h2>
                        <p className="mt-5 text-lg text-[var(--ink-soft)]">
                            Each one is handcrafted to your firm&apos;s workflows, data model, and voice — with more shipping every month.
                        </p>
                    </div>

                    {/* Counter + Arrow nav */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <span
                            data-testid="agents-counter"
                            className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--ink-dim)] tabular-nums"
                        >
                            {String(active + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={prev}
                                data-testid="agents-prev"
                                aria-label="Previous agent"
                                className="w-10 h-10 rounded-full border border-[var(--border-strong)] bg-[var(--paper)] flex items-center justify-center text-[var(--ink)] hover:bg-[var(--ink)] hover:text-white hover:border-[var(--ink)] transition-colors"
                            >
                                <i className="fa-solid fa-arrow-left text-xs" />
                            </button>
                            <button
                                type="button"
                                onClick={next}
                                data-testid="agents-next"
                                aria-label="Next agent"
                                className="w-10 h-10 rounded-full border border-[var(--border-strong)] bg-[var(--paper)] flex items-center justify-center text-[var(--ink)] hover:bg-[var(--ink)] hover:text-white hover:border-[var(--ink)] transition-colors"
                            >
                                <i className="fa-solid fa-arrow-right text-xs" />
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* Card stage */}
                <div
                    className="relative"
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onFocus={() => setPaused(true)}
                    onBlur={() => setPaused(false)}
                >
                    <div className="relative grid grid-cols-1 lg:grid-cols-[1.55fr_0.65fr] gap-6 md:gap-8 items-stretch">
                        {/* Active agent card — wrapped in a motion drag handler on mobile for physical card-swipe nav */}
                        <motion.div
                            className="relative min-h-[540px] md:min-h-[500px] touch-pan-y lg:cursor-default"
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={isMobile ? 0.55 : 0.18}
                            dragMomentum={false}
                            dragListener={isMobile}
                            style={isMobile ? { x: swipeX } : undefined}
                            onDragStart={() => setPaused(true)}
                            onDragEnd={(_, info) => {
                                const W = (typeof window !== "undefined" ? window.innerWidth : 400);
                                const T = 80;
                                const V = 400;
                                const willNext = info.offset.x < -T || info.velocity.x < -V;
                                const willPrev = info.offset.x > T || info.velocity.x > V;
                                const swipeOff = (toX, advance) => {
                                    // 1) fly current card off screen
                                    animateMv(swipeX, toX, { duration: 0.22, ease: [0.32, 0.72, 0, 1] }).then(() => {
                                        // 2) advance + teleport wrapper to opposite side
                                        advance();
                                        swipeX.jump(-toX);
                                        // 3) animate new card in from opposite side
                                        animateMv(swipeX, 0, { duration: 0.28, ease: [0.16, 1, 0.3, 1] });
                                    });
                                };
                                if (willNext)      swipeOff(-W, next);
                                else if (willPrev) swipeOff(W,  prev);
                                // Resume auto-rotate shortly after the swipe completes
                                setTimeout(() => setPaused(false), 700);
                            }}
                            data-testid="agents-swipe-stage"
                        >
                            <AnimatePresence mode="wait" custom={direction} initial={false}>
                                <AgentCard
                                    key={agent.name}
                                    agent={agent}
                                    direction={direction}
                                    index={active}
                                    silent={isMobile}
                                    onOpenDemo={(demoKey) => {
                                        setDemoAgent({ key: demoKey, name: agent.name });
                                        setDemoOpen(true);
                                        setDemoMinimized(false);
                                    }}
                                />
                            </AnimatePresence>
                        </motion.div>

                        {/* Side rail: list of agents (acts as picker on desktop) */}
                        <div className="hidden lg:flex flex-col gap-1.5">
                            {agents.map((a, i) => {
                                const isActive = i === active;
                                const isSoonRow = a.tint === "soon";

                                if (isSoonRow) {
                                    return (
                                        <button
                                            key={a.name}
                                            type="button"
                                            onClick={() => goTo(i)}
                                            data-testid={`agents-rail-${i}`}
                                            aria-current={isActive}
                                            aria-label={`Show ${a.name}`}
                                            className={`group relative text-left mt-3 px-4 py-3.5 rounded-xl flex items-center gap-3 overflow-hidden transition-all duration-300 ${
                                                isActive ? "ring-2 ring-[var(--blue)] ring-offset-2 ring-offset-[var(--cream)]" : ""
                                            }`}
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, #15171A 0%, #1E2024 60%, #1E5BFF 180%)",
                                                color: "#F7F6F5",
                                            }}
                                        >
                                            {/* sparkle accents */}
                                            <span className="absolute top-2 right-3 text-[var(--blue)] opacity-80">
                                                <i className="fa-solid fa-sparkles text-[9px]" />
                                            </span>
                                            <span className="absolute bottom-1.5 left-9 text-white/30">
                                                <i className="fa-solid fa-sparkle text-[7px]" />
                                            </span>

                                            <span className="font-mono text-[10px] tracking-[0.18em] text-[var(--blue)] tabular-nums w-6 font-semibold">
                                                ✦
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-display text-[13.5px] font-semibold leading-tight text-white truncate">
                                                    More shipping every month
                                                </div>
                                                <div className="font-mono text-[8.5px] tracking-[0.16em] uppercase text-white/55 mt-0.5">
                                                    Bespoke · Handcrafted
                                                </div>
                                            </div>
                                            <i className={`fa-solid ${isActive ? "fa-arrow-right" : "fa-wand-magic-sparkles"} text-[10px] text-[var(--blue)] ml-auto flex-shrink-0`} />
                                        </button>
                                    );
                                }

                                return (
                                    <button
                                        key={a.name}
                                        type="button"
                                        onClick={() => goTo(i)}
                                        data-testid={`agents-rail-${i}`}
                                        aria-current={isActive}
                                        aria-label={`Show ${a.name}`}
                                        className={`group text-left px-4 py-3 rounded-xl border transition-all duration-300 flex items-center gap-3 ${
                                            isActive
                                                ? "border-[var(--ink)] bg-[var(--paper)] shadow-[0_1px_0_rgba(0,0,0,0.04)]"
                                                : "border-[var(--border)] bg-transparent hover:bg-[var(--paper)] hover:border-[var(--border-strong)]"
                                        }`}
                                    >
                                        <span className="font-mono text-[10px] tracking-[0.18em] text-[var(--ink-dim)] tabular-nums w-6">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span
                                            className={`font-display text-[14px] font-semibold leading-tight ${
                                                isActive ? "text-[var(--ink)]" : "text-[var(--ink-soft)]"
                                            }`}
                                        >
                                            {a.name}
                                        </span>
                                        <div className="ml-auto flex items-center gap-2">
                                            {AGENT_DEMO_KEYS[a.name] && (
                                                <span
                                                    data-testid={`rail-demo-badge-${AGENT_DEMO_KEYS[a.name]}`}
                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[var(--blue-soft)] accent-blue text-[9px] font-mono tracking-[0.14em] uppercase font-semibold"
                                                >
                                                    <span className="relative flex h-1 w-1">
                                                        <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--blue)] opacity-70 animate-ping" />
                                                        <span className="relative inline-flex rounded-full h-1 w-1 bg-[var(--blue)]" />
                                                    </span>
                                                    Try demo
                                                </span>
                                            )}
                                            {isActive && (
                                                <i className="fa-solid fa-arrow-right text-[10px] text-[var(--ink)]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Progress bar + mobile dots */}
                    <div className="mt-8 flex items-center gap-4">
                        <div
                            data-testid="agents-progress"
                            className="relative h-[2px] flex-1 bg-[var(--border)] overflow-hidden rounded-full"
                        >
                            <motion.div
                                key={`bar-${active}-${paused}`}
                                initial={{ width: "0%" }}
                                animate={{ width: paused ? "0%" : "100%" }}
                                transition={{ duration: paused ? 0 : ROTATE_INTERVAL_MS / 1000, ease: "linear" }}
                                className="absolute inset-y-0 left-0 bg-[var(--ink)]"
                            />
                        </div>
                        <div className="flex gap-1.5 lg:hidden">
                            {agents.map((a, i) => (
                                <button
                                    key={a.name}
                                    type="button"
                                    onClick={() => goTo(i)}
                                    aria-label={`Show ${a.name}`}
                                    data-testid={`agents-dot-${i}`}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                        i === active ? "w-6 bg-[var(--ink)]" : "w-1.5 bg-[var(--border-strong)]"
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <AgentLiveDemo
                agentKey={demoAgent?.key}
                agentName={demoAgent?.name}
                open={demoOpen}
                minimized={demoMinimized}
                onOpen={() => {
                    setDemoOpen(true);
                    setDemoMinimized(false);
                }}
                onMinimize={() => {
                    setDemoOpen(false);
                    setDemoMinimized(true);
                }}
                onClose={() => {
                    setDemoOpen(false);
                    setDemoMinimized(false);
                }}
            />
        </section>
    );
};

const AgentCard = ({ agent, direction, index, silent = false, onOpenDemo }) => {
    const isBlue = agent.tint === "blue";
    const isSoon = agent.tint === "soon";
    const demoKey = AGENT_DEMO_KEYS[agent.name];
    const totalAgents = agents.length;
    // On mobile (silent), the wrapper handles the swipe; inner card mounts/unmounts without animation
    const motionProps = silent
        ? {
              initial: false,
              animate: { opacity: 1, x: 0, y: 0 },
              exit: { opacity: 1, x: 0, y: 0 },
              transition: { duration: 0 },
          }
        : {
              custom: direction,
              initial: { opacity: 0, y: 24, x: direction * 24 },
              animate: { opacity: 1, y: 0, x: 0 },
              exit: { opacity: 0, y: -16, x: direction * -24 },
              transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
          };
    return (
        <motion.article
            {...motionProps}
            data-testid={`agent-card-${index}`}
            className={`p-6 md:p-8 flex flex-col min-h-[520px] md:min-h-[480px] h-full rounded-2xl relative overflow-hidden ${
                isSoon ? "text-white" : "card-paper"
            }`}
            style={
                isSoon
                    ? {
                          background:
                              "radial-gradient(circle at 0% 0%, rgba(30,91,255,0.30) 0%, transparent 55%), linear-gradient(135deg, #15171A 0%, #1B1D21 45%, #14182A 100%)",
                      }
                    : undefined
            }
        >
            {/* Sparkles decoration — only on bespoke card */}
            {isSoon && (
                <>
                    <motion.span
                        aria-hidden
                        animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.15, 1] }}
                        transition={{ duration: 3, repeat: Infinity, delay: 0 }}
                        className="absolute top-8 right-10 accent-blue"
                    >
                        <i className="fa-solid fa-sparkle text-[10px]" />
                    </motion.span>
                    <motion.span
                        aria-hidden
                        animate={{ opacity: [0.3, 0.9, 0.3], scale: [1, 1.2, 1] }}
                        transition={{ duration: 2.5, repeat: Infinity, delay: 0.8 }}
                        className="absolute top-20 right-32 text-white/40"
                    >
                        <i className="fa-solid fa-sparkle text-[8px]" />
                    </motion.span>
                    <motion.span
                        aria-hidden
                        animate={{ opacity: [0.3, 0.8, 0.3], scale: [1, 1.18, 1] }}
                        transition={{ duration: 2.8, repeat: Infinity, delay: 1.6 }}
                        className="absolute bottom-24 left-12 accent-blue"
                    >
                        <i className="fa-solid fa-sparkle text-[9px]" />
                    </motion.span>
                </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-[1fr_1.05fr] gap-6 md:gap-7 h-full relative z-10">
                {/* Left: copy */}
                <div className="flex flex-col">
                    <div className="flex items-start justify-between mb-5">
                        <div
                            className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                                isSoon
                                    ? "bg-[var(--blue)] text-white shadow-[0_0_28px_rgba(30,91,255,0.55)]"
                                    : isBlue
                                    ? "bg-[var(--blue-soft)] accent-blue"
                                    : "bg-[var(--cream-2)] text-[var(--ink)]"
                            }`}
                        >
                            <i className={`fa-solid ${agent.icon} text-lg`} />
                        </div>
                        <span
                            className={`font-mono text-[10px] tracking-[0.22em] uppercase tabular-nums mt-2 ${
                                isSoon ? "text-[var(--blue)]" : "text-[var(--ink-dim)]"
                            }`}
                        >
                            {isSoon ? "Bespoke · Handcrafted" : `Agent · ${String(index + 1).padStart(2, "0")}`}
                        </span>
                    </div>

                    <h3
                        className={`font-display text-3xl md:text-[2rem] mb-3 leading-[1.05] font-semibold tracking-tight ${
                            isSoon ? "text-white" : "text-[var(--ink)]"
                        }`}
                    >
                        {isSoon ? (
                            <>
                                Custom-built for{" "}
                                <span className="accent-blue">your firm.</span>
                            </>
                        ) : (
                            agent.name
                        )}
                    </h3>

                    <p
                        className={`leading-relaxed mb-6 text-[15px] ${
                            isSoon ? "text-white/75" : "text-[var(--ink-soft)]"
                        }`}
                    >
                        {agent.tagline}
                    </p>

                    <ul className="space-y-2.5 mb-6">
                        {agent.bullets.map((b) => (
                            <li
                                key={b}
                                className={`flex items-start gap-3 text-[14px] leading-snug ${
                                    isSoon ? "text-white/90" : "text-[var(--ink)]"
                                }`}
                            >
                                <i
                                    className={`fa-solid ${isSoon ? "fa-sparkle" : "fa-check"} text-xs mt-1.5 flex-shrink-0 ${
                                        isBlue || isSoon ? "accent-blue" : "text-[var(--ink)]"
                                    }`}
                                />
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>

                    {demoKey && !isSoon && (
                        <button
                            type="button"
                            onClick={() => onOpenDemo(demoKey)}
                            data-testid={`demo-trigger-${demoKey}`}
                            className="self-start inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[var(--blue)]/25 bg-[var(--blue-soft)] accent-blue text-[12px] font-medium hover:bg-[var(--blue)] hover:text-white hover:border-[var(--blue)] transition-colors group/demo"
                        >
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--blue)] opacity-70 animate-ping" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--blue)] group-hover/demo:bg-white" />
                            </span>
                            Try a live demo
                            <i className="fa-solid fa-arrow-right text-[9px] transition-transform group-hover/demo:translate-x-0.5" />
                        </button>
                    )}

                    <div className="flex-1" />

                    {/* CTA / Footer */}
                    {isSoon ? (
                        <div className="pt-5 border-t border-white/15 flex items-center gap-3">
                            <a
                                href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="bespoke-cta"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[var(--blue)] text-white font-medium text-[13px] hover:bg-white hover:text-[var(--ink)] transition-colors"
                            >
                                Pitch us your bottleneck
                                <i className="fa-solid fa-arrow-right text-[10px]" />
                            </a>
                            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/55 ml-auto hidden sm:inline">
                                ✦ Magic happens here
                            </span>
                        </div>
                    ) : (
                        <div className="pt-5 border-t border-[var(--border)] flex items-center gap-2 text-xs font-mono text-[var(--ink-dim)]">
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--blue)] opacity-75 animate-ping" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--blue)]" />
                            </span>
                            LIVE · 24/7
                            <span className="ml-auto tabular-nums">
                                {String(index + 1).padStart(2, "0")} / {String(totalAgents).padStart(2, "0")}
                            </span>
                        </div>
                    )}
                </div>

                {/* Right: animated visual */}
                <div className="relative md:h-full flex items-stretch">
                    <div className="w-full h-full min-h-[280px]">
                        <AgentVisual name={agent.name} />
                    </div>
                </div>
            </div>
        </motion.article>
    );
};

export default AgentsSection;
